import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { androidColors } from '../../theme/androidTheme';

interface CryptoData {
  id: string;
  symbol: string;
  name: string;
  amount: number;
  price_buy: number;
  price_current: number;
  value_current: number;
  value_initial: number;
  gain_perte: number;
  gain_perte_pourcentage: number;
  icon?: string;
}

interface CryptoListItemProps {
  crypto: CryptoData;
  index: number;
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  isDashboard?: boolean;
}

const CryptoListItem: React.FC<CryptoListItemProps> = ({
  crypto,
  index,
  onEdit,
  onDelete,
  isDashboard = false,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) onEdit(index);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) onDelete(index);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const isPositive = crypto.gain_perte >= 0;

  return (
    <Card
      sx={{
        backgroundColor: androidColors.cardBackground,
        border: `1px solid ${androidColors.borderColor}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: androidColors.cardBackgroundLight,
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px rgba(241, 192, 134, 0.1)`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Avatar de la crypto */}
          <Avatar
            src={crypto.icon || `https://cdn.cryptonvx.com/icons-data/${crypto.symbol.toLowerCase()}.png`}
            alt={crypto.name}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: `${androidColors.primaryGold}20`,
              border: `2px solid ${androidColors.borderColor}`,
            }}
          >
            {crypto.symbol.charAt(0)}
          </Avatar>

          {/* Informations principales */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  color: androidColors.textColor,
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {crypto.name || crypto.symbol.toUpperCase()}
              </Typography>
              <Chip
                label={crypto.symbol.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: `${androidColors.primaryGold}20`,
                  color: androidColors.primaryGold,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  height: 20,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: androidColors.textSecondary,
                fontSize: '0.875rem',
              }}
            >
              {crypto.amount} {crypto.symbol.toUpperCase()} • {formatCurrency(crypto.price_current)}
            </Typography>
          </Box>

          {/* Valeurs et performance */}
          <Box sx={{ textAlign: 'right', minWidth: 120 }}>
            <Typography
              variant="h6"
              sx={{
                color: androidColors.textColor,
                fontWeight: 700,
                fontSize: '1rem',
                mb: 0.5,
              }}
            >
              {formatCurrency(crypto.value_current)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              {isPositive ? (
                <TrendingUp sx={{ color: androidColors.success, fontSize: 16 }} />
              ) : (
                <TrendingDown sx={{ color: androidColors.error, fontSize: 16 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: isPositive ? androidColors.success : androidColors.error,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {formatPercentage(crypto.gain_perte_pourcentage)}
              </Typography>
            </Box>
          </Box>

          {/* Menu d'actions */}
          {!isDashboard && (onEdit || onDelete) && (
            <Box>
              <IconButton
                onClick={handleMenuClick}
                sx={{
                  color: androidColors.textSecondary,
                  '&:hover': {
                    backgroundColor: `${androidColors.primaryGold}10`,
                    color: androidColors.primaryGold,
                  },
                }}
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    backgroundColor: androidColors.cardBackground,
                    border: `1px solid ${androidColors.borderColor}`,
                    borderRadius: 2,
                    minWidth: 150,
                  },
                }}
              >
                {onEdit && (
                  <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                      <Edit sx={{ color: androidColors.primaryGold }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('common.edit')}
                      sx={{ color: androidColors.textColor }}
                    />
                  </MenuItem>
                )}
                {onDelete && (
                  <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                      <Delete sx={{ color: androidColors.error }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('common.delete')}
                      sx={{ color: androidColors.error }}
                    />
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CryptoListItem;